import dynamic from 'next/dynamic'
import * as caseStudyQuery from './queries/casestudy'
import * as frontPageQuery from './queries/front-page'
import * as pageQuery from './queries/page'
import * as blogPageQuery from './queries/page-blog'
import * as careersPageQuery from './queries/page-careers'
import * as citgoPageQuery from './queries/page-citgo'
import * as contactPageQuery from './queries/page-contact'
import * as leadershipPageQuery from './queries/page-leadership'
import * as motechPageQuery from './queries/page-motivation-technologies'
import * as workPageQuery from './queries/page-work'
import * as singleQuery from './queries/single'

/**
 * Template views are code-split so each page only downloads the template it renders, rather than
 * every template (and every component they use) shipping in `_app`. Faust needs a template's
 * `query`/`variables` before rendering, so those live in light `./queries/*` modules.
 *
 * Each `import()` must be written inline in `dynamic()` so Next can server-render the view and
 * preload its chunk before hydration.
 */
const views = {
  'front-page': dynamic(() => import('./front-page')),
  page: dynamic(() => import('./page')),
  'page-blog': dynamic(() => import('./page-blog')),
  'page-careers': dynamic(() => import('./page-careers')),
  'single-casestudy': dynamic(() => import('./casestudy')),
  'page-work': dynamic(() => import('./page-work')),
  'page-contact': dynamic(() => import('./page-contact')),
  'page-citgo': dynamic(() => import('./page-citgo')),
  'page-leadership': dynamic(() => import('./page-leadership')),
  'page-motivation-technologies': dynamic(() => import('./page-motivation-technologies')),
  single: dynamic(() => import('./single'))
}

// Faust treats a `next/dynamic` component handed to it directly as a "dynamic template" and renders
// nothing for it on the server, so give it a plain wrapper component instead.
function withQuery(View, { query, variables }) {
  function Template(props) {
    return <View {...props} />
  }
  Template.query = query
  Template.variables = variables
  return Template
}

/**
 * Load the remaining template chunks in the background, so a client-side navigation to a page using
 * a different template doesn't render an empty page while that template's chunk downloads.
 */
export function preloadTemplates() {
  Object.values(views).forEach((View) => View.render?.preload?.())
}

export default {
  'front-page': withQuery(views['front-page'], frontPageQuery),
  page: withQuery(views.page, pageQuery),
  'page-blog': withQuery(views['page-blog'], blogPageQuery),
  'page-careers': withQuery(views['page-careers'], careersPageQuery),
  'single-casestudy': withQuery(views['single-casestudy'], caseStudyQuery),
  'page-work': withQuery(views['page-work'], workPageQuery),
  'page-contact': withQuery(views['page-contact'], contactPageQuery),
  'page-citgo': withQuery(views['page-citgo'], citgoPageQuery),
  'page-leadership': withQuery(views['page-leadership'], leadershipPageQuery),
  'page-motivation-technologies': withQuery(views['page-motivation-technologies'], motechPageQuery),
  single: withQuery(views.single, singleQuery)
}
